import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import { Loader } from '@rfacil/shared/ui';

interface AuthRedirectProps {
  to?: string;
  /** Calcula el destino al autenticarse (p. ej. enlace pendiente); `null` cae en `to`. Se llama una sola vez. */
  resolveTo?: () => string | null;
  children?: React.ReactNode;
}

/**
 * @component AuthRedirect
 * @description Un componente que redirige a los usuarios autenticados fuera de las páginas públicas como la página de inicio de sesión.
 * Si el usuario está autenticado, lo redirige a una ruta especificada (por defecto, al panel de control).
 * También muestra un spinner de carga mientras comprueba el estado de autenticación.
 * @param {object} props Las propiedades del componente.
 * @param {string} [props.to='/'] La ruta a la que redirigir si el usuario está autenticado.
 * @param {() => string | null} [props.resolveTo] Destino calculado al autenticarse; se consume una sola vez y, si devuelve `null`, se usa `to`.
 * @param {React.ReactNode} [props.children] Los componentes secundarios a renderizar si el usuario no está autenticado.
 * @returns {React.ReactElement | null} El componente renderizado o nulo si se está redirigiendo.
 */
export const AuthRedirect: React.FC<AuthRedirectProps> = ({
  to = '/',
  resolveTo,
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  // `resolveTo` puede tener efectos (consumir el enlace pendiente): la ref evita que StrictMode
  // o un nuevo render lo ejecuten dos veces.
  const destinoResuelto = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      destinoResuelto.current ??= resolveTo?.() ?? to;
      navigate(destinoResuelto.current, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, to, resolveTo]);

  if (isLoading) {
    return (
      <Loader
        message="Validando tu sesión..."
        subtitle="Por favor espera unos segundos"
      />
    );
  }

  if (isAuthenticated) {
    return null; // Se redirigirá automáticamente
  }

  return <>{children}</>;
};

export default AuthRedirect;
